import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Row, Select, Space, Input, Button, Table, Typography, Popover, Tooltip, notification } from 'antd';
import { PlusOutlined, DeleteOutlined, MoreOutlined, FormOutlined, EditFilled, ExportOutlined, IdcardOutlined, MailOutlined, ReloadOutlined, TeamOutlined, MobileOutlined, WalletOutlined } from '@ant-design/icons';
import { addUser, changeRole, changeState, editUser, getUsers, isUserExistError, loadWalletPassDetails, resendWelcomeEmail, revokeAccessCredential, revokeWalletCredential, resumeWalletCredential, suspendWalletCredential } from './actions';
import { sessionStore, SESSION_ATTRS } from '../store/session';
import { ACCESS_CREDENTIAL_MODE, DEFAULT_ACCESS_CREDENTIAL_POLICY, USER_MODAL_TYPE, USER_ROLE, USER_STATE, USER_STATE_ARRAY, CSV_EXPORT_FIELDS, ADMIN_MODAL_TYPE } from './constants';
import { toDateOnlyString } from '../utils/date-time';
import { confirm, notifiableAPICall, notifyError, notifyInProgress, notifySuccess, notifyWarning } from '../utils/notification';
import ImportUserModal from './ImportUserModal';
import NewUserModal from './NewUserModal';
import ConfirmationModal from '../components/ConfirmationModal';
import InceptionDuplicateUserModal from './InceptionDuplicateUserModal';
import download from 'downloadjs';
import { getUserServiceColumns } from './serviceIntegrations';
import { getInceptionDuplicateUserErrorData, isInceptionDuplicateUserError } from './serviceIntegrations/inceptionActions';
import WalletCredentialList from './WalletCredentialList';
import { WalletCredential, getWalletCredentialDescription, getWalletPlatformLabel } from './walletCredentials';
import { isTenantWalletEnabled } from '../utils/tenantFeatures';

const { Parser } = require('json2csv')

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Title } = Typography;
const opts = { fields: CSV_EXPORT_FIELDS }
const ALL = "all";
const user_default = {
    userId: '',
    tenantId: '',
    buildingId: '',
    firstName: '',
    lastName: '',
    contactNumber: '',
    countryDialingCode: undefined,
    email: '',
    accessCredentialPolicy: DEFAULT_ACCESS_CREDENTIAL_POLICY,
    visitorPassDelegationEnabled: false
}

type UserListRefreshButtonProps = {
    loading: boolean;
    onRefresh: () => void | Promise<void>;
};

export const UserListRefreshButton = ({ loading, onRefresh }: UserListRefreshButtonProps) => (
    <Button
        type="default"
        className="btn btn-bordered user-list-refresh"
        icon={<ReloadOutlined />}
        loading={loading}
        disabled={loading}
        aria-label={loading ? 'Refreshing users' : 'Refresh users'}
        onClick={onRefresh}
    >
        Refresh
    </Button>
);

export const Users = () => {
    const { state } = useContext(sessionStore);
    const buildings: any = state.get(SESSION_ATTRS.BUILDINGS);
    const tenants = (state.get(SESSION_ATTRS.TENANTS) as any[] | undefined) || [];
    const showCredentialTypeFilter = tenants.some(isTenantWalletEnabled);
    const tenantIdNameMapping: any = state.get(SESSION_ATTRS.TENANT_ID_NAMES);
    const buildingIdNameMapping: any = state.get(SESSION_ATTRS.BUILDING_ID_NAMES);
    const loggedUser: any = state.get(SESSION_ATTRS.LOGGED_USER);

    const [users, setUsers] = useState<Array<any>>([]);
    const [selectedUser, setSelectedUsers] = useState<any[]>([]);
    const [searchingPhrase, setSearchingPhrase] = useState<string | undefined>();
    const [selectedState, setSelectedState] = useState<number>(USER_STATE.ACTIVE);
    const [selectedBuilding, setSelectedBuilding] = useState<string>(buildings[0].buildingId);
    const [selectedCredentialMode, setSelectedCredentialMode] = useState<string>(ALL);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isShowNewUserModal, setShowNewUserModal] = useState<boolean>(false);
    const [isShowImportUserModal, setShowImportUserModal] = useState<boolean>(false);
    const [isSavingUser, setIsSavingUser] = useState<boolean>(false);
    const [mode, setMode] = useState(USER_MODAL_TYPE.CREATE);
    const [editRowData, setEditRowData] = useState<any>(user_default);
    const [isAdminModalVisible, setIsAdminModalVisible] = useState(false);
    const [adminModalType, setAdminModalType] = useState(ADMIN_MODAL_TYPE.ASSIGN);
    const [userRoleChangeData, setUserRoleChangeData] = useState<any>({});
    const [isResendEmailModalVisible, setIsResendEmailModalVisible] = useState(false);
    const [resendEmailData, setResendEmailData] = useState<{ tenantId: string; userId: string } | null>(null);
    const [isInceptionDuplicateModalVisible, setIsInceptionDuplicateModalVisible] = useState(false);
    const [inceptionDuplicateData, setInceptionDuplicateData] = useState<any>(null);
    const [pendingCreateUserValues, setPendingCreateUserValues] = useState<any>(null);
    const [selectedInceptionDuplicateUserId, setSelectedInceptionDuplicateUserId] = useState<string>('');

    const prevStateRef = useRef<number | undefined>();

    const tenantsArrayFilter = (selectTenants: Array<string> = []) => {
        return selectTenants.map((t: string) => tenantIdNameMapping[t]).filter(t => !!t).join(', ');
    }

    const getBuildingName = (buildingId: string) => {
        return buildingIdNameMapping[buildingId];
    }

    const filteredUsers = useMemo(() => {
        let filtered = users;
        if (searchingPhrase) {
            const search = searchingPhrase.toLowerCase();
            filtered = filtered.filter(f => `${f.firstName} ${f.lastName}`.toLowerCase().includes(search));
        }
        if (selectedBuilding !== ALL) {
            filtered = filtered.filter(f => f.buildingId === selectedBuilding);
        }
        if (showCredentialTypeFilter && selectedCredentialMode !== ALL) {
            filtered = filtered.filter(f => (
                f.credentialType || f.accessCredentialPolicy?.mode || DEFAULT_ACCESS_CREDENTIAL_POLICY.mode
            ) === selectedCredentialMode);
        }
        return filtered;
    }, [users, searchingPhrase, selectedBuilding, selectedCredentialMode, showCredentialTypeFilter]);

    const loadUsers = useCallback(async (state: number) => {
        try {
            setLoading(true);
            let userList = await getUsers(state, selectedBuilding);
            userList.sort((n1: any, n2: any) => (new Date(n2.createdAt)).getTime() - (new Date(n1.createdAt)).getTime());
            setUsers(userList);
            setLoading(false);
        } catch (e) {
            console.error(e);
            setUsers([]);
            setLoading(false);
        }
    }, [selectedBuilding]);

    useEffect(() => {
        if (prevStateRef.current !== selectedState) {
            prevStateRef.current = selectedState;
            loadUsers(selectedState);
        }
    }, [loadUsers, selectedState]);

    const onSearch = (value: string | undefined) => {
        setSearchingPhrase(value);
        setSelectedUsers([]);
    };

    const onStateChange = (value: number) => {
        setSelectedState(value);
        setSelectedUsers([]);
    }

    const onBuildingChange = (value: string) => {
        setSelectedBuilding(value);
        if (value !== ALL) setSelectedUsers([]);
    }

    const onCredentialModeChange = (value: string) => {
        setSelectedCredentialMode(value);
        setSelectedUsers([]);
    }

    const statusMapper = (state: number) => {
        return state === USER_STATE.ACTIVE ? <span className="status status--active"> Active </span> : <span className="status status--inactive"> Inactive </span>;
    }

    const onConvertRole = async (userId: string, tenantId: string, currRole: string, buildingId: string) => {
        await notifiableAPICall(async () => {
            await changeRole(userId, tenantId, currRole, buildingId);
            await loadUsers(selectedState);
        },
            "user-role-change",
            "Please wait...",
            "Changed user role successfully.",
            "Something went wrong in changing user role. Please retry."
        );
    }

    const onChangeState = async (state: number, userId: string, tenantId: string, buildingId: string) => {
        confirm(state === USER_STATE.ACTIVE ? "Deactivate User" : "Activate User",
            state === USER_STATE.ACTIVE ? "Are you sure you want to deactivate this user?" : "Are you sure you want to activate this user?",
            async () => {
                await notifiableAPICall(async () => {
                    try {
                        await changeState(userId, tenantId, buildingId);
                    } finally {
                        await loadUsers(selectedState);
                        if (userId === loggedUser.userId) {
                            window.location.reload();
                        }
                    }
                },
                    "user-state-change",
                    "Please wait...",
                    "Changed user status successfully.",
                    "Something went wrong in change user status. Please retry."
                );
            }, () => { });

    }

    const onBulkChangeState = async () => {
        confirm(selectedState === USER_STATE.ACTIVE ? "Deactivate Users" : "Activate Users",
            selectedState === USER_STATE.ACTIVE ? "Are you sure you want to deactivate selected users?" : "Are you sure you want to activate selected users?",
            async () => {
                await notifiableAPICall(async () => {
                    try {
                        const proms = selectedUser.map(async ({ userId, tenantId, buildingId }) =>
                            await changeState(userId, tenantId, buildingId));
                        await Promise.all(proms);
                    } finally {
                        setSelectedUsers([]);
                        await loadUsers(selectedState);
                    }
                },
                    "user-bulk-state-change",
                    "Please wait...",
                    "Changed user status successfully.",
                    "Something went wrong in changing one or more user's state. Please retry."
                );
            }, () => { });
    }

    const getCredentialRevokeCopy = (accessCredentialPolicy: any = DEFAULT_ACCESS_CREDENTIAL_POLICY) => {
        switch (accessCredentialPolicy?.mode) {
            case ACCESS_CREDENTIAL_MODE.WALLET:
                return {
                    label: 'Revoke All Wallet Credentials',
                    title: 'Revoke All Wallet Credentials',
                    description: "This is a user-level action and will revoke every iPhone, Apple Watch, and Google Wallet pass belonging to this user. Use the action beside a specific pass to revoke only that device.",
                    success: 'All Wallet credentials successfully revoked.',
                    failure: "Something went wrong in revoking one or more of the user's Wallet credentials. Please retry.",
                };
            case ACCESS_CREDENTIAL_MODE.MOBILE_APP:
            default:
                return {
                    label: 'Revoke Mobile Credential',
                    title: 'Revoke Mobile Credential',
                    description: "This will require the user to reactivate their mobile credential within the app. Once issued, their new mobile credential will need to be configured in the building's security system. Use this function if the user is having issues activating their mobile credential.",
                    success: 'Mobile credential successfully revoked.',
                    failure: "Something went wrong in revoking the user's mobile credential. Please retry.",
                };
        }
    };

    const isWalletUser = (row: any) => (row.credentialType || row.accessCredentialPolicy?.mode) === ACCESS_CREDENTIAL_MODE.WALLET;

    const onRevokeAccessCredential = async (row: any) => {
        const revokeCopy = getCredentialRevokeCopy(row.accessCredentialPolicy);
        confirm(revokeCopy.title,
            revokeCopy.description,
            async () => {
                await notifiableAPICall(
                    async () => {
                        try {
                            await revokeAccessCredential(row.userId, row.tenantId, row.buildingId);
                        } finally {
                            await loadUsers(selectedState);
                        }
                    },
                    "user-revoke-access-credential",
                    "Please wait...",
                    revokeCopy.success,
                    revokeCopy.failure
                );
            }, () => { }
        );
    }

    const onSuspendWalletCredential = async (row: any, credential: WalletCredential) => {
        if (!credential.passId) {
            notifyError("This Wallet pass is still waiting for its HID identity and cannot be suspended yet.", "wallet-pass-suspend-missing-id");
            return;
        }

        const description = getWalletCredentialDescription(credential);
        confirm(`Suspend ${getWalletPlatformLabel(credential.platform, credential.deviceType)}`,
            `This will suspend only ${description}. Other Wallet passes for this user will remain unchanged.`,
            async () => {
                await notifiableAPICall(
                    async () => {
                        try {
                            await suspendWalletCredential(credential.passId!);
                        } finally {
                            await loadUsers(selectedState);
                        }
                    },
                    `wallet-pass-suspend-${credential.passId}`,
                    "Suspending Wallet credential...",
                    "Wallet credential successfully suspended.",
                    "Something went wrong in suspending this Wallet credential. Please retry."
                );
            }, () => { }
        );
    }

    const onResumeWalletCredential = async (row: any, credential: WalletCredential) => {
        if (!credential.passId) {
            notifyError("This Wallet pass is still waiting for its HID identity and cannot be resumed yet.", "wallet-pass-resume-missing-id");
            return;
        }

        const description = getWalletCredentialDescription(credential);
        confirm(`Resume ${getWalletPlatformLabel(credential.platform, credential.deviceType)}`,
            `This will resume only ${description}. Other Wallet passes for this user will remain unchanged.`,
            async () => {
                await notifiableAPICall(
                    async () => {
                        try {
                            await resumeWalletCredential(credential.passId!);
                        } finally {
                            await loadUsers(selectedState);
                        }
                    },
                    `wallet-pass-resume-${credential.passId}`,
                    "Resuming Wallet credential...",
                    "Wallet credential successfully resumed.",
                    "Something went wrong in resuming this Wallet credential. Please retry."
                );
            }, () => { }
        );
    }

    const onRevokeWalletCredential = async (row: any, credential: WalletCredential) => {
        if (!credential.passId) {
            notifyError("This Wallet pass is still waiting for its HID identity and cannot be revoked yet.", "wallet-pass-revoke-missing-id");
            return;
        }

        const description = getWalletCredentialDescription(credential);
        confirm(`Revoke ${getWalletPlatformLabel(credential.platform, credential.deviceType)}`,
            `This will permanently revoke only ${description}. Other Wallet passes for this user will remain unchanged.`,
            async () => {
                await notifiableAPICall(
                    async () => {
                        try {
                            await revokeWalletCredential(credential.passId!);
                        } finally {
                            await loadUsers(selectedState);
                        }
                    },
                    `wallet-pass-revoke-${credential.passId}`,
                    "Revoking Wallet credential...",
                    "Wallet credential successfully revoked.",
                    "Something went wrong in revoking this Wallet credential. Please retry."
                );
            }, () => { }
        );
    }

    const onResendWelcomeEmail = async (tenantId: string, userId: string) => {
        setResendEmailData({ tenantId, userId });
        setIsResendEmailModalVisible(true);
    }

    const handleResendEmailConfirm = async () => {
        if (!resendEmailData) return;

        setIsResendEmailModalVisible(false);
        await notifiableAPICall(
            async () => {
                await resendWelcomeEmail(resendEmailData.tenantId, resendEmailData.userId);
            },
            "resend-welcome-email",
            "Sending welcome email...",
            "Welcome email sent successfully.",
            "Something went wrong in sending the welcome email. Please retry."
        );
        setResendEmailData(null);
    }

    const handleResendEmailCancel = () => {
        setIsResendEmailModalVisible(false);
        setResendEmailData(null);
    }

    const editModalOpen = async (row: any) => {
        resetInceptionDuplicateState();
        setMode(USER_MODAL_TYPE.EDIT);
        const walletCredentials = [
            ...(Array.isArray(row.walletCredentials) ? row.walletCredentials : []),
            ...(Array.isArray(row.walletCredentialHistory) ? row.walletCredentialHistory : []),
        ];
        setEditRowData(isWalletUser(row) ? { ...row, walletCredentials } : row);
        setShowNewUserModal(true);

        if (isWalletUser(row)) {
            const {
                reconciliationSucceeded,
                reconciliationError,
                walletCredentials,
                walletPassLoadError
            } = await loadWalletPassDetails(row.tenantId, row.userId, !!row.hidWalletUserId);

            if (row.hidWalletUserId && !reconciliationSucceeded) {
                console.error('Unable to reconcile Wallet passes for user details', reconciliationError);
                notifyWarning('Unable to repair missing device passes right now. Showing locally stored Wallet passes.', 8);
            }

            if (walletCredentials) {
                setEditRowData((currentRow: any) => currentRow?.userId === row.userId
                    && currentRow?.tenantId === row.tenantId
                    ? { ...currentRow, walletCredentials }
                    : currentRow);
            }

            if (walletPassLoadError) {
                console.error('Unable to refresh Wallet passes for user details', walletPassLoadError);
                notifyError('Unable to refresh Wallet pass history. Showing the latest available user data.', `wallet-pass-history-${row.userId}`);
            }

            if (reconciliationSucceeded) {
                await loadUsers(selectedState);
            }
        }
    }

    const onUserAddManually = () => {
        setShowImportUserModal(false);
        setEditRowData(user_default);
        setMode(USER_MODAL_TYPE.CREATE);
        resetInceptionDuplicateState();
        setShowNewUserModal(true);
    }

    const isInBulkArray = (user: any) => {
        return selectedUser.includes(user);
    }

    const resetInceptionDuplicateState = () => {
        setIsInceptionDuplicateModalVisible(false);
        setInceptionDuplicateData(null);
        setPendingCreateUserValues(null);
        setSelectedInceptionDuplicateUserId('');
    }

    const closeNewUserModal = () => {
        resetInceptionDuplicateState();
        setShowNewUserModal(false);
    }

    const getCreateUserErrorMessage = (e: any) => {
        if (isUserExistError(e)) return 'User already exists.';
        return e?.response?.data?.message || e?.message || 'Something went wrong, Please try again.';
    }

    const submitCreateUser = async (values: any, resolution: any = {}) => {
        if (isSavingUser) return;

        const notificationKey = 'add_new_user';
        setIsSavingUser(true);
        try {
            notifyInProgress('Please wait!', notificationKey);
            await addUser({
                ...values,
                ...resolution
            });
            notification.close(notificationKey);
            notifySuccess('user added successfully.', notificationKey);
            closeNewUserModal();
            await loadUsers(selectedState);
        } catch (e: any) {
            notification.close(notificationKey);
            if (isInceptionDuplicateUserError(e)) {
                const duplicateData = getInceptionDuplicateUserErrorData(e);
                const duplicateUsers = duplicateData?.duplicateUsers || [];
                const linkCandidate = duplicateData?.linkCandidate || duplicateUsers[0] || null;

                setPendingCreateUserValues(values);
                setInceptionDuplicateData(duplicateData);
                setSelectedInceptionDuplicateUserId(linkCandidate?.inceptionUserId || '');
                setIsInceptionDuplicateModalVisible(true);
                return;
            }

            notifyError(getCreateUserErrorMessage(e), notificationKey);
        } finally {
            setIsSavingUser(false);
        }
    }

    const onLinkDuplicateInceptionUser = async () => {
        if (!pendingCreateUserValues || !selectedInceptionDuplicateUserId) {
            return;
        }

        await submitCreateUser(pendingCreateUserValues, {
            inceptionDuplicateAction: 'link',
            inceptionDuplicateUserId: selectedInceptionDuplicateUserId
        });
    }

    const onCreateNewInceptionUser = async () => {
        if (!pendingCreateUserValues) {
            return;
        }

        await submitCreateUser(pendingCreateUserValues, {
            inceptionDuplicateAction: 'create'
        });
    }

    const onSave = async (values: any) => {
        if (isSavingUser) return;
        if (mode === USER_MODAL_TYPE.CREATE) {
            await submitCreateUser(values);
            return;
        }

        setIsSavingUser(true);
        try {
            if (mode !== USER_MODAL_TYPE.CREATE) {
                let data: any = values;
                data.userId = editRowData.userId;
                data.createdAt = editRowData.createdAt;
                data.role = editRowData.role;
                data.state = editRowData.state;

                await notifiableAPICall(async () => await editUser(data),
                    'edit_user',
                    'Please wait!',
                    'Updated user details successfully.',
                    'Something went wrong, Please try again.',
                    true
                );
            }
            closeNewUserModal();
            await loadUsers(selectedState);
        } finally {
            setIsSavingUser(false);
        }
    }

    const onBulkUserAdd = async () => {
        setShowImportUserModal(false);
        await loadUsers(selectedState);
    }

    const exportableUsers = () => {
        let exportUsersList = [];
        exportUsersList = filteredUsers.map(item => {
            let eul = {
                firstName: item.firstName,
                lastName: item.lastName,
                email: item.email,
                countryDialingCode: item.countryDialingCode,
                contactNumber: item.contactNumber,
                licensePlate: item.licensePlate,
                tenant: tenantsArrayFilter([item.tenantId]),
                building: getBuildingName(item.buildingId),
                status: (item.state === USER_STATE.ACTIVE) ? 'Active' : 'Inactive',
                createdDate: toDateOnlyString(item.createdAt),
                credentialId: Array.isArray(item.credentialIds) && item.credentialIds.length
                    ? item.credentialIds.join(', ')
                    : item.credentialId,
            }
            return eul;
        });

        return exportUsersList;
    }

    const convertToCsv = () => {
        try {
            const parser = new Parser(opts);
            const csv = parser.parse(exportableUsers());
            const file = new Blob([csv], { type: 'text/csv' });
            download(file, 'Users.csv');
        } catch (err) {
            console.error(err);
        }
    }

    const rowActions = (userId: string, row: any) => {
        let content = (
            <>
                <Button className='intnnt-ppvr-btn' type='text' icon={<EditFilled />} onClick={() => editModalOpen(row)}>Edit</Button>
                {!row.isFederatedUser && <Button className='intnnt-ppvr-btn' type='text' icon={<FormOutlined />} onClick={() => openAdminModal(row.userId, row.tenantId, row.role, row.buildingId)}>{row.role !== USER_ROLE.ADMIN ? 'Assign Admin' : 'Unassign Admin'}</Button>}
                {row.state === USER_STATE.ACTIVE && <Button className='intnnt-ppvr-btn' type='text' icon={<IdcardOutlined />} onClick={() => onRevokeAccessCredential(row)}>{getCredentialRevokeCopy(row.accessCredentialPolicy).label}</Button>}
                {row?.needsPasswordChange && !row.isFederatedUser && (
                    <Button className='intnnt-ppvr-btn' type='text' icon={<MailOutlined />} onClick={() => onResendWelcomeEmail(row.tenantId, row.userId)}>
                        Resend Welcome Email
                    </Button>
                )}
                <Button className='intnnt-ppvr-btn' type='text' danger icon={<DeleteOutlined />} onClick={() => onChangeState(row.state, row.userId, row.tenantId, row.buildingId)}>{row.state !== USER_STATE.ACTIVE ? 'Activate' : 'Deactivate'}</Button>
            </>
        );

        return (
            <div className='intnnt-ppvr'>
                {content}
            </div>
        )
    };

    const isAdmin = (role: string) => {
        if (role === USER_ROLE.ADMIN) {
            return (
                // StarFilled, StarOutlined, StarTwoTone,CrownFilled
                <Tooltip title="Admin"><span style={{ color: '#faad14', marginLeft: 4, fontSize: 20 }}><>★</></span></Tooltip>
                // <Tooltip title='Admin'><StarFilled style={{ color: '#104EA1' }} /></Tooltip>
            )
        }
    }

    const onAdminConfirmationOk = async () => {
        await onConvertRole(userRoleChangeData.userId, userRoleChangeData.tenantId,
            userRoleChangeData.currRole,
            userRoleChangeData.buildingId);
        setIsAdminModalVisible(false);
        if (userRoleChangeData.userId === loggedUser.userId) {
            window.location.reload();
            return false;
        }
    }

    const onAdminConfirmationCancel = () => {
        setIsAdminModalVisible(false);
    }

    const openAdminModal = (userId: string, tenantId: string, currRole: string, buildingId: string) => {
        if (currRole === USER_ROLE.USER) {
            setAdminModalType(ADMIN_MODAL_TYPE.ASSIGN);
        } else {
            setAdminModalType(ADMIN_MODAL_TYPE.Unassign);
        }
        setUserRoleChangeData({ userId, tenantId, currRole, buildingId });
        setIsAdminModalVisible(true);
    }

    const getFullName = (row: any) => row.firstName ? (row.firstName + ' ' + row.lastName + ' ') : "-";

    const moreAction = (userId: string, row: any) => isInBulkArray(row) ?
        <div className="ind-more"><MoreOutlined style={{ color: '#cecece' }} className="more-settings" /></div>
        :
        <Popover trigger='hover' placement='left' content={rowActions(userId, row)}>
            <div className="ind-more"><MoreOutlined className="more-settings" /></div>
        </Popover>

    const renderBlankCredential = () => <span>&nbsp;</span>

    const renderCredential = (credentialId: string, row: any) => {
        const walletCredentials = Array.isArray(row.walletCredentials) ? row.walletCredentials : [];
        if ((row.credentialType || row.accessCredentialPolicy?.mode) === ACCESS_CREDENTIAL_MODE.WALLET && walletCredentials.length) {
            return (
                <WalletCredentialList
                    credentials={walletCredentials}
                    actionsEnabled={row.state === USER_STATE.ACTIVE}
                    maxVisible={2}
                    onSuspend={(credential) => onSuspendWalletCredential(row, credential)}
                    onResume={(credential) => onResumeWalletCredential(row, credential)}
                    onRevoke={(credential) => onRevokeWalletCredential(row, credential)}
                />
            );
        }

        return credentialId ? <span>{credentialId}</span> : renderBlankCredential();
    }

    const columns: Array<any> = [
        {
            title: 'Full Name',
            dataIndex: 'name',
            sorter: (a: any, b: any) => getFullName(a).localeCompare(getFullName(b)),
            render: (userId: string, row: any) => (
                <span
                    className="table-title-link"
                    onClick={(e) => { e.stopPropagation(); editModalOpen(row); }}
                >
                    {getFullName(row)}{isAdmin(row.role)}
                </span>
            ),
        },
        {
            title: 'Tenant',
            dataIndex: 'tenantId',
            sorter: (a: any, b: any) => tenantsArrayFilter([a.tenantId]).localeCompare(tenantsArrayFilter([b.tenantId])),
            render: (tenantId: string) => <span className="tenantsArrayFilter">{tenantsArrayFilter([tenantId])}</span>,
        },
        {
            title: 'Building',
            dataIndex: 'buildingId',
            sorter: (a: any, b: any) => getBuildingName(a.buildingId).localeCompare(getBuildingName(b.buildingId)),
            render: (buildingId: string) => getBuildingName(buildingId),
        },
        {
            title: 'Contact No.',
            dataIndex: 'contactNo',
            render: (userId: string, row: any) => <span>{row.contactNumber ? (row.countryDialingCode + ' ' + row.contactNumber) : "-"}</span>,
        },
        {
            title: 'Credential',
            dataIndex: 'credentialId',
            width: 280,
            render: renderCredential
        },
        {
            title: 'Email',
            dataIndex: 'email'
        },
        ...getUserServiceColumns(filteredUsers),
        {
            title: 'Status',
            dataIndex: 'state',
            render: (state: number) => statusMapper(state),
        },
        {
            title: 'Created Date',
            dataIndex: 'createdAt',
            render: (createdAt: string) => <span>{toDateOnlyString(createdAt)}</span>
        },
        {
            title: '',
            dataIndex: 'newsId',
            key: 'newsId',
            fixed: 'right',
            render: ((userId: string, row: any) => (
                moreAction(userId, row)
            ))
        }
    ];

    const rowSelection = {
        selectedRowKeys: selectedUser.map(i => i.userId),
        onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
            setSelectedUsers(selectedRows);
        }
    };

    const tableHeader = () => {
        if (selectedUser.length === 0) return null;
        let content = null;
        switch (selectedState) {
            case USER_STATE.ACTIVE:
                content = (<>
                    <Button type="text" className="link-btn link-btn--danger" danger icon={<DeleteOutlined />} onClick={onBulkChangeState}>Deactivate</Button>
                </>);
                break;
            case USER_STATE.INACTIVE:
                content = (<>
                    <Button type="text" className="link-btn link-btn--danger" danger icon={<DeleteOutlined />} onClick={onBulkChangeState}>Activate</Button>
                </>);
                break;
            default:
                content = null;
        }
        return (<Space>
            <span className="select-text">{selectedUser.length} selected</span>
            <span className="divider-vertical"></span>
            {content}
        </Space>);
    }

    const AdminConfirmationModal = isAdminModalVisible ?
        <ConfirmationModal
            visible={isAdminModalVisible}
            title={adminModalType}
            message={adminModalType === ADMIN_MODAL_TYPE.ASSIGN ? "This person will now have admin permissions." : "Do you want to continue?"}
            confirmText="Confirm"
            cancelText="Cancel"
            onConfirm={onAdminConfirmationOk}
            onCancel={onAdminConfirmationCancel}
        />
        :
        null

    return (
        <>
            {isShowImportUserModal && <ImportUserModal
                onClose={() => setShowImportUserModal(false)}
                onUserAddManually={onUserAddManually}
                onBulkUserAddSucess={onBulkUserAdd}
                onUserAdd={addUser}
            />}
            {
                isShowNewUserModal && <NewUserModal
                    onClose={closeNewUserModal}
                    onSubmit={onSave}
                    mode={mode}
                    editRowData={editRowData}
                    isLoading={isSavingUser}
                />
            }
            <InceptionDuplicateUserModal
                visible={isInceptionDuplicateModalVisible}
                duplicateUsers={inceptionDuplicateData?.duplicateUsers || []}
                message={inceptionDuplicateData?.message}
                selectedUserId={selectedInceptionDuplicateUserId}
                onSelectUser={setSelectedInceptionDuplicateUserId}
                onLinkUser={onLinkDuplicateInceptionUser}
                onCreateNewUser={onCreateNewInceptionUser}
                onCancel={() => setIsInceptionDuplicateModalVisible(false)}
                isSubmitting={isSavingUser}
            />
            {
                isAdminModalVisible && AdminConfirmationModal
            }
            <ConfirmationModal
                visible={isResendEmailModalVisible}
                title="Resend Welcome Email"
                message="Are you sure you want to resend the welcome email to this user? This will send a new email with login instructions."
                confirmText="Send Email"
                cancelText="Cancel"
                onConfirm={handleResendEmailConfirm}
                onCancel={handleResendEmailCancel}
            />
            <Space direction='vertical'>
                <Space direction='vertical' style={{ width: '100%' }}>
                    <Row style={{ marginTop: 15, justifyContent: "space-between" }}>
                        <Title level={4}>
                            {filteredUsers.length} Users
                        </Title>
                        <Button type='link' style={{ color: '#104EA1', fontSize: 12, fontWeight: 600 }} icon={<ExportOutlined style={{ color: '#104EA1', fontWeight: 600 }} />} onClick={convertToCsv}>Export .CSV</Button>
                    </Row>
                    <Row className="grid-options" align="bottom">
                        <Space wrap align="end">
                            <Select defaultValue={USER_STATE.ACTIVE} onChange={onStateChange} >
                                {USER_STATE_ARRAY.map(({ name, state }) => <Option key={state} value={state}>{name}</Option>)}
                            </Select>
                            <Select defaultValue={buildings[0].buildingId} onChange={onBuildingChange} >
                                {/* <Option value={ALL}>All Buildings</Option> */}
                                {buildings.map((building: any) => (
                                    <Option key={building.buildingId} value={building.buildingId}>{building.name}</Option>
                                ))}
                            </Select>
                            {showCredentialTypeFilter && (
                                <div className={`user-credential-filter${selectedCredentialMode !== ALL ? ' user-credential-filter--active' : ''}`}>
                                    <Select
                                        id="user-credential-mode"
                                        aria-label="Filter by credential type"
                                        className="user-credential-filter__select"
                                        popupClassName="user-credential-filter-dropdown"
                                        value={selectedCredentialMode}
                                        onChange={onCredentialModeChange}
                                    >
                                        <Option value={ALL} aria-label="All credential types">
                                            <span className="user-credential-filter__option"><TeamOutlined /><span>All credential types</span></span>
                                        </Option>
                                        <Option value={ACCESS_CREDENTIAL_MODE.MOBILE_APP} aria-label="Mobile App">
                                            <span className="user-credential-filter__option"><MobileOutlined /><span>Mobile App</span></span>
                                        </Option>
                                        <Option value={ACCESS_CREDENTIAL_MODE.WALLET} aria-label="Wallet">
                                            <span className="user-credential-filter__option"><WalletOutlined /><span>Wallet</span></span>
                                        </Option>
                                    </Select>
                                </div>
                            )}
                        </Space>
                        <Space className="grid-options--search">
                            <Search placeholder="Search" allowClear onChange={(e) => onSearch(e.target.value)} style={{ width: 400 }} />
                            <UserListRefreshButton
                                loading={isLoading}
                                onRefresh={() => loadUsers(selectedState)}
                            />
                            {/*loggedUser?.serviceNowAdmin === true && (
                                <Button
                                    className="btn btn--primary"
                                    onClick={onSyncInceptionUsers}
                                    loading={isSyncingInceptionUsers}
                                >
                                    Sync Inception Users
                                </Button>
                            )*/}
                            <Button
                                className="btn btn--primary"
                                icon={<PlusOutlined />}
                                onClick={() => setShowImportUserModal(true)}
                            >
                                Add New
                            </Button>
                        </Space>
                    </Row>
                </Space>
                <Content className="table-wrapper">
                    <Table rowSelection={rowSelection}
                        columns={columns}
                        dataSource={filteredUsers}
                        loading={isLoading}
                        rowKey='userId'
                        title={tableHeader}
                        pagination={{
                            total: filteredUsers.length,
                            showSizeChanger: true,
                            showTotal: (total, range) => `showing ${range[0]}-${range[1]} out of ${total} users`
                        }} />
                </Content>
            </Space>
        </>
    )
};
